import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { MapPin, Calendar, Users, Check, X, FileText } from "lucide-react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/hooks/useLanguage";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import Autoplay from "embla-carousel-autoplay";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { useState } from "react";

const Tours = () => {
  const { t, language } = useLanguage();
  const [openItineraryDialog, setOpenItineraryDialog] = useState<string | null>(null);
  
  const { data: tours, isLoading } = useQuery({
    queryKey: ["tours"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("tours")
        .select("*")
        .eq("is_active", true)
        .order("is_featured", { ascending: false });
      
      if (error) throw error;
      return data;
    },
  });

  const structuredData = tours && tours.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "African Safari Tour Packages",
    "description": "Curated safari tours across Southern Africa including South Africa, Botswana, Namibia, and Zimbabwe",
    "numberOfItems": tours.length,
    "itemListElement": tours.map((tour, index) => ({
      "@type": "TouristTrip",
      "position": index + 1,
      "name": tour.name,
      "description": tour.short_description,
      "touristType": "Safari enthusiasts",
      "provider": {
        "@type": "TravelAgency",
        "name": "Shikwaru Adventures",
        "url": "https://shikwaru-adventures.com",
        "telephone": "+27 73 2563514",
        "email": "info@shikwaru-adventures.com"
      },
      "offers": {
        "@type": "Offer",
        "price": tour.price_per_person,
        "priceCurrency": "USD",
        "availability": "https://schema.org/InStock",
        "validFrom": new Date().toISOString().split('T')[0]
      },
      "itinerary": {
        "@type": "ItemList",
        "name": tour.destination,
        "numberOfItems": tour.duration_days
      },
      "duration": `P${tour.duration_days}D`,
      "maximumAttendeeCapacity": tour.max_group_size || 10
    }))
  } : undefined;

  return (
    <div className="min-h-screen">
      <SEO
        title="African Safari Tour Packages | Cape Town, Kruger Park, Okavango Delta & Victoria Falls"
        description="Browse our curated safari tours: Cape Town & Kruger combination, Botswana parks & Victoria Falls, Zebra Migration, Garden Route & safaris. 12-14 day adventures with expert guides. Camping, guesthouse & lodge options from $5,850pp. Small groups 2-10 people. Fully guided in English, Spanish & French."
        keywords="safari tour packages Africa, Cape Town Kruger safari, Botswana Victoria Falls tour, Okavango Delta safari, Kruger Park safari tours, Garden Route safari package, zebra migration Botswana, Namibia desert tours, camping safari packages, luxury lodge safari, small group safari tours, private safari South Africa, custom safari itinerary"
        structuredData={structuredData}
        canonical="/tours"
      />
      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="pt-32 pb-20 bg-gradient-to-b from-secondary/30 to-background">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-5xl font-bold mb-6">{t("toursPage.title")}</h1>
            <p className="text-xl text-muted-foreground mb-8">
              {t("toursPage.subtitle")}
            </p>
            <Link to="/contact">
              <Button size="lg" className="bg-accent hover:bg-accent/90">
                {t("toursPage.requestCustom")}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Tours Grid */}
      <section className="py-16 bg-background">
        <div className="container mx-auto px-4">
          {isLoading ? (
            <div className="space-y-12">
              {[1, 2, 3].map((i) => (
                <Card key={i}>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <Skeleton className="w-full h-96" />
                    <div className="p-6 space-y-4">
                      <Skeleton className="h-8 w-3/4" />
                      <Skeleton className="h-4 w-full" />
                      <Skeleton className="h-20 w-full" />
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          ) : (
            <div className="space-y-12">
              {tours?.map((tour) => {
                const name = language === "es" ? tour.name : language === "en" ? (tour.name_en || tour.name) : (tour.name_fr || tour.name);
                const shortDesc = language === "es" ? tour.short_description : language === "en" ? (tour.short_description_en || tour.short_description) : (tour.short_description_fr || tour.short_description);
                const fullDesc = language === "es" ? tour.full_description : language === "en" ? (tour.full_description_en || tour.full_description) : (tour.full_description_fr || tour.full_description);
                const destination = language === "es" ? tour.destination : language === "en" ? (tour.destination_en || tour.destination) : (tour.destination_fr || tour.destination);
                const includedServices = language === "es" ? tour.included_services : language === "en" ? (tour.included_services_en || tour.included_services) : (tour.included_services_fr || tour.included_services);
                const excludedServices = language === "es" ? tour.excluded_services : language === "en" ? (tour.excluded_services_en || tour.excluded_services) : (tour.excluded_services_fr || tour.excluded_services);
                
                return (
                  <Card key={tour.id} className="overflow-hidden hover:shadow-2xl transition-all">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
                      {/* Image Carousel */}
                      <div className="relative">
                        {tour.gallery_images && tour.gallery_images.length > 0 ? (
                          <Carousel 
                            className="w-full"
                            plugins={[
                              Autoplay({
                                delay: 4000,
                                stopOnInteraction: true,
                              }),
                            ]}
                          >
                            <CarouselContent>
                              {tour.gallery_images.map((imageUrl: string, index: number) => (
                                <CarouselItem key={index}>
                                  <img
                                    src={imageUrl}
                                    alt={`${name} - ${index + 1}`}
                                    className="w-full h-full object-cover min-h-96"
                                  />
                                </CarouselItem>
                              ))}
                            </CarouselContent>
                            {tour.gallery_images.length > 1 && (
                              <>
                                <CarouselPrevious className="left-4" />
                                <CarouselNext className="right-4" />
                              </>
                            )}
                          </Carousel>
                        ) : (
                          <img
                            src={tour.image_url}
                            alt={name}
                            className="w-full h-full object-cover min-h-96"
                          />
                        )}
                        {tour.is_featured && (
                          <Badge className="absolute top-4 right-4 bg-accent">
                            {t("toursPage.mostPopular")}
                          </Badge>
                        )}
                      </div>

                      {/* Content */}
                      <div className="p-8 flex flex-col justify-between">
                        <div>
                          <CardHeader className="p-0 mb-6">
                            <CardTitle className="text-3xl mb-3">{name}</CardTitle>
                            <CardDescription className="text-lg">
                              {shortDesc}
                            </CardDescription>
                          </CardHeader>

                          <CardContent className="p-0 space-y-6">
                            <p className="text-muted-foreground leading-relaxed">
                              {fullDesc}
                            </p>

                            {/* Tour Details */}
                            <div className="grid grid-cols-2 gap-4">
                              <div className="flex items-center text-muted-foreground">
                                <MapPin className="mr-2 text-primary" size={20} />
                                <span>{destination}</span>
                              </div>
                              <div className="flex items-center text-muted-foreground">
                                <Calendar className="mr-2 text-primary" size={20} />
                                <span>{tour.duration_days} {t("tours.days")}</span>
                              </div>
                              <div className="flex items-center text-muted-foreground">
                                <Users className="mr-2 text-primary" size={20} />
                                <span>
                                  {tour.min_group_size && tour.min_group_size !== tour.max_group_size 
                                    ? `${tour.min_group_size}-${tour.max_group_size}` 
                                    : tour.max_group_size || 12} {t("common.people")}
                                </span>
                              </div>
                            </div>

                            {/* Itinerary Button */}
                            {tour.itinerary && Array.isArray(tour.itinerary) && tour.itinerary.length > 0 && (
                              <Dialog open={openItineraryDialog === tour.id} onOpenChange={(open) => setOpenItineraryDialog(open ? tour.id : null)}>
                                <DialogTrigger asChild>
                                  <Button variant="outline" className="w-full">
                                    <FileText className="mr-2 h-4 w-4" />
                                    {t("itinerary.view")}
                                  </Button>
                                </DialogTrigger>
                                <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                                  <DialogHeader>
                                    <DialogTitle className="text-2xl">{t("itinerary.title")} - {name}</DialogTitle>
                                    <DialogDescription>
                                      {tour.duration_days} {t("tours.days")} {t("itinerary.adventure")}
                                    </DialogDescription>
                                  </DialogHeader>
                                  <div className="space-y-4 mt-4">
                                    {(tour.itinerary as any[]).map((day: any, index: number) => {
                                      const dayDescription = language === "es" 
                                        ? day.description_es 
                                        : language === "en" 
                                        ? (day.description_en || day.description_es)
                                        : (day.description_fr || day.description_es);
                                      
                                      return (
                                        <div key={index} className="border-l-4 border-primary pl-4 py-3 bg-secondary/20 rounded-r-lg">
                                          <p className="font-bold text-base mb-2 text-primary">{t("itinerary.day")} {day.day}</p>
                                          <p className="text-sm text-foreground leading-relaxed">{dayDescription}</p>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </DialogContent>
                              </Dialog>
                            )}

                            {/* Services Grid - Side by Side */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                              {/* Included Services */}
                              {includedServices && includedServices.length > 0 && (
                                <div>
                                  <h4 className="font-semibold mb-3 text-primary">{t("toursPage.whatsIncluded")}</h4>
                                  <div className="space-y-2">
                                    {includedServices.map((service, index) => (
                                      <div key={index} className="flex items-start">
                                        <Check className="mr-2 text-primary flex-shrink-0" size={18} />
                                        <span className="text-sm text-muted-foreground">{service}</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* Excluded Services */}
                              {excludedServices && excludedServices.length > 0 && (
                                <div>
                                  <h4 className="font-semibold mb-3 text-muted-foreground">{t("toursPage.notIncluded")}</h4>
                                  <div className="space-y-2">
                                    {excludedServices.map((service, index) => (
                                      <div key={index} className="flex items-start">
                                        <X className="mr-2 text-muted-foreground flex-shrink-0" size={18} />
                                        <span className="text-sm text-muted-foreground">{service}</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </CardContent>
                        </div>

                        {/* Price and CTA */}
                        <div className="mt-8 pt-6 border-t border-border">
                          <div className="flex items-end justify-between mb-4">
                            <div>
                              <p className="text-sm text-muted-foreground mb-1">{t("toursPage.from")}</p>
                              <p className="text-4xl font-bold text-primary">
                                ${tour.price_per_person}
                              </p>
                              <p className="text-sm text-muted-foreground">{t("tours.perPerson")}</p>
                            </div>
                          </div>
                          <Link to={`/contact?tour=${encodeURIComponent(name)}`}>
                            <Button className="w-full" size="lg">
                              {t("toursPage.inquireNow")}
                            </Button>
                          </Link>
                        </div>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}

          {/* Custom Tours CTA */}
          <div className="mt-16 text-center">
            <Card className="max-w-2xl mx-auto bg-gradient-to-r from-secondary/50 to-secondary/30">
              <CardHeader>
                <CardTitle className="text-3xl">{t("toursPage.lookingDifferent")}</CardTitle>
                <CardDescription className="text-lg">
                  {t("toursPage.tailorMade")}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Link to="/contact">
                  <Button size="lg" variant="outline" className="border-primary hover:bg-primary hover:text-primary-foreground">
                    {t("toursPage.createCustom")}
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
      </main>

      <Footer />
    </div>
  );
};

export default Tours;
